// TODO(#23079): The seam the sidebar uses to read/write "relates to". Repoint at `fragment.topRelatesTo` +
// Relay mutations and delete `mocks/relates-to` once the GraphQL API lands (see github/github-ui#26754).

import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useCallback, useMemo, useSyncExternalStore} from 'react'

import {escapeOptimisticTitleHTML} from '../../utils/helpers'
import {
  addRelatesTo as addRelatesToStore,
  getRelatesToSnapshot,
  removeRelatesTo as removeRelatesToStore,
  subscribe,
} from './relates-to-mock-store'
import type {RelatesToConnection, RelatesToIssueNode} from './relates-to-types'

export const RELATES_TO_FEATURE_FLAG = 'issues_relates_to_relationship'

// Mirrors the real bounded `topRelatesTo(first: N)` connection; matches the sibling `VISIBLE_ISSUES_THRESHOLD`.
// The overflow (`hasNextPage`) is reachable via the "view all" modal, which reads `relatesToNodes` (full set).
const TOP_RELATES_TO_LIMIT = 3

const EMPTY_CONNECTION: RelatesToConnection = {nodes: [], pageInfo: {hasNextPage: false}}

const NOOP_SUBSCRIBE = () => () => {}

export type AddRelatesToInput = {
  id: string
  title: string
  url: string
  number: number
  state: string
  stateReason?: string | null
  repositoryNameWithOwner: string
}

function toRelatesToNode(input: AddRelatesToInput): RelatesToIssueNode {
  const [owner, name] = input.repositoryNameWithOwner.split('/')
  return {
    id: input.id,
    number: input.number,
    title: input.title,
    // Escape plaintext picker titles before writing to titleHTML (the row renders it as trusted HTML).
    titleHTML: escapeOptimisticTitleHTML(input.title),
    url: input.url,
    state: input.state,
    stateReason: input.stateReason ?? null,
    repository: {
      nameWithOwner: input.repositoryNameWithOwner,
      name: name ?? '',
      owner: {login: owner ?? ''},
    },
  }
}

export type UseRelatesToResult = {
  topRelatesTo: RelatesToConnection
  // Full node set for the "view all" modal — the mock holds every relation in memory, so no pagination.
  relatesToNodes: readonly RelatesToIssueNode[]
  // Full id set for the picker's selection/diffing — the blockedBy/blocking `first: 100` query analog.
  relatesToIssueIds: string[]
  enabled: boolean
  addRelatesTo: (input: AddRelatesToInput) => Promise<void>
  removeRelatesTo: (relatedIssueId: string) => Promise<void>
}

// Flag off: returns an empty connection and no-op mutators, so nothing renders and behavior is unchanged.
// Pass `subscribed: false` for callers that only need the mutators (avoids re-rendering on store changes).
export function useRelatesTo(issueId: string, subscribed = true): UseRelatesToResult {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     The monolith publishes this flag to the issues-react app payload via `add_client_feature_flag`
     (repo/org/user gated), so it must be read with `useFeatureFlag`, not `isFeatureEnabled`. */
  const enabled = useFeatureFlag(RELATES_TO_FEATURE_FLAG)
  const active = enabled && subscribed

  const nodes = useSyncExternalStore(
    active ? subscribe : NOOP_SUBSCRIBE,
    () => getRelatesToSnapshot(issueId),
    () => getRelatesToSnapshot(issueId),
  )

  const addRelatesTo = useCallback(
    async (input: AddRelatesToInput) => {
      if (!enabled) return
      await addRelatesToStore(issueId, toRelatesToNode(input))
    },
    [enabled, issueId],
  )

  const removeRelatesTo = useCallback(
    async (relatedIssueId: string) => {
      if (!enabled) return
      await removeRelatesToStore(issueId, relatedIssueId)
    },
    [enabled, issueId],
  )

  const topRelatesTo = useMemo<RelatesToConnection>(() => {
    if (!active) return EMPTY_CONNECTION
    return {
      nodes: nodes.slice(0, TOP_RELATES_TO_LIMIT),
      pageInfo: {hasNextPage: nodes.length > TOP_RELATES_TO_LIMIT},
    }
  }, [active, nodes])

  const relatesToNodes = useMemo(() => (active ? nodes : []), [active, nodes])

  const relatesToIssueIds = useMemo(() => (active ? nodes.map(node => node.id) : []), [active, nodes])

  return {topRelatesTo, relatesToNodes, relatesToIssueIds, enabled, addRelatesTo, removeRelatesTo}
}
